import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AlertTriangle, Eye, RefreshCw, Trash2, Flame, Droplets, Wind, Mountain } from "lucide-react";

export const ELEMENT_COLORS = { fire: "#ef4444", water: "#06b6d4", wind: "#10b981", earth: "#d97706" };
export const ELEMENT_ICONS = { fire: Flame, water: Droplets, wind: Wind, earth: Mountain };

export const ScenarioChoicesBadge = ({ choices, scenarioId }) => {
  const scenarioChoices = choices
    ?.filter(c => c.scenario_id === scenarioId)
    ?.sort((a, b) => a.stage - b.stage) || [];

  if (scenarioChoices.length === 0) return <span className="text-gray-400 text-xs">-</span>;

  return (
    <div className="flex gap-0.5">
      {scenarioChoices.map((choice) => {
        const Icon = ELEMENT_ICONS[choice.element];
        return (
          <div
            key={`s${scenarioId}-stg${choice.stage}-${choice.element}`}
            className="w-5 h-5 rounded flex items-center justify-center"
            style={{ backgroundColor: ELEMENT_COLORS[choice.element] }}
            title={`Stage ${choice.stage}: ${choice.element}`}
          >
            {Icon && <Icon className="w-3 h-3 text-white" />}
          </div>
        );
      })}
    </div>
  );
};

export const AdminStatsCard = ({ assessments, selectedCount, loading, onRefresh }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-lg flex items-center justify-between">
        <span>Assessment Overview</span>
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-3xl font-bold text-blue-600">{assessments.length}</p>
          <p className="text-sm text-gray-600">Total Entries</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <p className="text-3xl font-bold text-green-600">{assessments.filter(a => a.completed).length}</p>
          <p className="text-sm text-gray-600">Completed</p>
        </div>
        <div className="text-center p-4 bg-yellow-50 rounded-lg">
          <p className="text-3xl font-bold text-yellow-600">{assessments.filter(a => !a.completed).length}</p>
          <p className="text-sm text-gray-600">In Progress</p>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <p className="text-3xl font-bold text-purple-600">{selectedCount}</p>
          <p className="text-sm text-gray-600">Selected</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const AssessmentRow = ({ assessment, selected, onSelect, onViewReport, onDelete }) => (
  <TableRow>
    <TableCell>
      <Checkbox
        checked={selected}
        onCheckedChange={(checked) => onSelect(assessment.id, checked)}
        aria-label={`Select ${assessment.user_name}`}
      />
    </TableCell>
    <TableCell className="font-medium">{assessment.user_name || 'Anonymous'}</TableCell>
    <TableCell>{assessment.gender || '-'}</TableCell>
    <TableCell>{assessment.age_range || '-'}</TableCell>
    <TableCell>{assessment.years_experience || '-'}</TableCell>
    {[1, 2, 3, 4, 5].map(scenarioId => (
      <TableCell key={scenarioId} className="text-center">
        <ScenarioChoicesBadge choices={assessment.choices} scenarioId={scenarioId} />
      </TableCell>
    ))}
    <TableCell>
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        assessment.completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
      }`}>
        {assessment.completed ? 'Completed' : 'In Progress'}
      </span>
    </TableCell>
    <TableCell className="text-sm text-gray-600">
      {new Date(assessment.created_at).toLocaleDateString()}
    </TableCell>
    <TableCell className="text-right">
      <div className="flex justify-end gap-2">
        {assessment.completed && (
          <Button variant="ghost" size="sm" onClick={() => onViewReport(assessment.id)} title="View Report">
            <Eye className="w-4 h-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => onDelete(assessment.id)}
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </TableCell>
  </TableRow>
);

export const ElementLegendCard = () => (
  <Card>
    <CardContent className="py-4">
      <div className="flex flex-wrap items-center gap-6 justify-center">
        <span className="text-sm text-gray-600 font-medium">Element Legend:</span>
        {Object.entries(ELEMENT_ICONS).map(([el, Icon]) => (
          <div key={el} className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: ELEMENT_COLORS[el] }}>
              <Icon className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm capitalize">{el}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const getDeleteTitle = (deleteTarget, selectedCount) => {
  if (deleteTarget === 'all') return 'Delete All Assessments?';
  if (deleteTarget === 'selected') return `Delete ${selectedCount} Selected Assessments?`;
  return 'Delete Assessment?';
};

const getDeleteDesc = (deleteTarget, totalAssessments, selectedCount) => {
  if (deleteTarget === 'all') return ` This will permanently delete all ${totalAssessments} assessments and their associated data.`;
  if (deleteTarget === 'selected') return ` This will permanently delete ${selectedCount} selected assessments and their associated data.`;
  return ' This will permanently delete the assessment and all associated data.';
};

export const DeleteConfirmationDialog = ({ open, onOpenChange, deleteTarget, totalAssessments, selectedCount, onConfirm }) => {
  const titleText = getDeleteTitle(deleteTarget, selectedCount);
  const descText = getDeleteDesc(deleteTarget, totalAssessments, selectedCount);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            {titleText}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone.{descText}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onConfirm}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
